<script setup lang="ts">
import { normalizeExternalVideoURL } from './common'
import type { UploadedFile } from '~/types'

const props = defineProps<{
  file: UploadedFile
  allowDelete: boolean
  alt: string
  iconSize: string
  isPdf: boolean
  isVideo: boolean
  isExternalVideo: boolean
}>()

defineEmits<{
  delete: []
}>()

/** Images, internal videos */
const isInternalVideo = computed(() => props.isVideo && !props.isExternalVideo)
const hasPreview = computed(() => props.file.hasPreview || isInternalVideo.value || props.isPdf)

const provider = computed(() => (
  props.isVideo
    ? 'videoThumbnail'
    : props.isPdf
      ? 'pdfThumbnail'
      : /* use default */ undefined
))

const modifiers = computed(() => props.isVideo ? { percentage: '5' } : undefined)
const quality = computed(() => props.file.hasPreview || props.isPdf ? '30' : undefined)
</script>

<template>
  <div class="FileContainer group relative">
    <!-- Parameters below are:
      - 145px is the image width defined in `components/File/Preview.vue`,
        - for high DPI screens (Retina, etc.) 290px, i.e. 2x, will be fetched;
      - quality 30% is an arbitrary value balancing quality and compression;
      - format is WEBP for modern browsers, and will fall back to JPEG for other;
      - lazy loading uses browser-native feature: https://developer.mozilla.org/en-US/docs/Web/API/HTMLIFrameElement/loading#lazy
      - classes are applied to both `<picture>` and `<img>` tag to properly display the image.

      Images (for compressed images, see `app/imageProvider.ts`):
      - quality 30% is an arbitrary value balancing quality and compression;

      Videos (for video thumbnails, see `app/videoThumbnailProvider.ts`):
      - percentage 5% is selected to avoid the initial intros present in most HOS videos;
    -->
    <NuxtPicture
      v-if="hasPreview"
      :src="file.filekey"
      :alt
      :provider
      :quality
      :modifiers
      sizes="145px"
      format="webp"
      loading="lazy"
      :img-attrs="{
        class: 'object-cover cursor-pointer h-full w-full',
      }"
      class="object-cover cursor-pointer h-full w-full"
    />
    <Icon v-else-if="isVideo" name="material-symbols:smart-display-rounded" size="3rem" class="DocumentIcon text-red-700 rounded" />
    <Icon v-else name="material-symbols:note-outline-rounded" :size="iconSize" class="DocumentIcon text-blue-700 rounded" />

    <!-- Play icon for the video -->
    <div v-if="isInternalVideo" class="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
      <Icon name="material-symbols:play-circle-rounded" size="small" class="p-5 text-white rounded" />
    </div>

    <div class="hidden group-hover:flex absolute right-[8px] top-[8px] gap-1" @click.stop>
      <nuxt-link :href="isExternalVideo ? normalizeExternalVideoURL(file.filekey) : file.filekey" target="_blank" rel="noopener">
        <n-button small color="#8a2be2">
          <template #icon>
            <Icon v-if="isExternalVideo" name="material-symbols:open-in-new-rounded" />
            <Icon v-else name="material-symbols:download" />
          </template>
        </n-button>
      </nuxt-link>
      <n-button v-if="allowDelete" small color="red" @click="$emit('delete')">
        <template #icon>
          <Icon name="material-symbols:close" />
        </template>
      </n-button>
    </div>
  </div>
</template>

<style scoped>
.FileContainer {
  @apply bg-gray-200 w-full aspect-square flex justify-center items-center rounded overflow-hidden
}
</style>
